import {
  Accordion,
  Box,
  Flex,
  Heading,
  Skeleton,
  Spacer,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { getErrorMessage } from '@finley-technologies/common';

import { ConfiguredCdqGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  useDeactivateCdqConfigMutation,
  useGetActiveCdqConfigsQuery,
} from './cdq.gql.generated';
import {
  CollateralDataQualityCheckAddMenu,
  CollateralDataQualityCheckNoConfigsDisplay,
} from './CollateralDataQualityCheckComponents';
import { CollateralDataQualityCheckAddEditCdqDrawerContainer } from './drawer/CollateralDataQualityCheckAddEditCdqDrawerContainer';
import { placeholderSQLString, CDQCount } from './utils';
import { CollateralDataQualityCheckResults } from '~/core/routes/collateral-data-quality/CollateralDataQualityCheckResults';
import { CDQChartPanel } from '~/core/routes/collateral-data-quality/PieChart';
import {
  errorToastConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';

export const CollateralDataQualityCheckMainContent = ({
  receivablesRoutePrefix,
  activeCDQs,
  handleArchive,
}: {
  receivablesRoutePrefix: string;
  activeCDQs: ConfiguredCdqGQLType[];
  handleArchive: (args: { configId: string }) => Promise<void>;
}) => {
  // we keep count of acknowledged and unacknowledged cdq results
  // these are fetched by individual cdq child components are updated here by updateCounts
  // callback below
  const [issueCounts, setIssueCounts] = useState<Record<string, CDQCount>>({});

  // this is passed to child components to update their counts
  const updateIssueCounts = useCallback((cdqId: string, cdqCount: CDQCount) => {
    setIssueCounts((prevCounts) => ({
      ...prevCounts,
      [cdqId]: cdqCount,
    }));
  }, []);

  const totalIssueCounts = useMemo(() => {
    if (!Object.keys(issueCounts).length) {
      return { issuesAcknowledged: 0, issuesUnacknowledged: 0, loading: true };
    }
    // aggregate all issue counts from the individual cdq components
    return Object.values(issueCounts).reduce<CDQCount>(
      (acc, cdqCount) => {
        // eslint-disable-next-line no-param-reassign
        acc.issuesAcknowledged += cdqCount.issuesAcknowledged;
        // eslint-disable-next-line no-param-reassign
        acc.issuesUnacknowledged += cdqCount.issuesUnacknowledged;
        // eslint-disable-next-line no-param-reassign
        acc.loading = acc.loading || cdqCount.loading;
        return acc;
      },
      { issuesAcknowledged: 0, issuesUnacknowledged: 0, loading: false },
    );
  }, [issueCounts]);

  return (
    <Box>
      <CDQChartPanel {...totalIssueCounts} />
      <Accordion allowMultiple>
        <VStack spacing="16px" w="100%" alignItems="left" mt="24px">
          {activeCDQs.map((cdqConfig) => {
            const { id, name, decodedQuery } = cdqConfig;
            return (
              <CollateralDataQualityCheckResults
                key={name}
                cdqConfigId={id}
                cdqConfigQuery={decodedQuery}
                cdqConfigName={name}
                handleArchive={handleArchive}
                updateIssueCounts={updateIssueCounts}
                isDeactivateCdqConfigLoading={false} // remove this prop
                receivablesRoutePrefix={receivablesRoutePrefix}
              />
            );
          })}
        </VStack>
      </Accordion>
    </Box>
  );
};

export const CollateralDataQualityCheck = ({
  organizationId,
  receivablesRoutePrefix,
}: {
  organizationId: string;
  receivablesRoutePrefix: string;
}) => {
  const toast = useToast();
  const {
    data: cdqConfigData,
    loading: isCdqConfigDataLoading,
    refetch: refetchCdqConfigs,
  } = useGetActiveCdqConfigsQuery({
    variables: {
      organizationId,
    },
    fetchPolicy: 'network-only',
    onError: (err) => {
      const errorMessage = getErrorMessage(err);
      toast({
        ...errorToastConfig,
        title: `Error fetching collateral data quality checks: ${errorMessage}`,
      });
    },
  });

  const [
    deactivateCdqConfig,
    { loading: isDeactivateCdqConfigLoading },
  ] = useDeactivateCdqConfigMutation();

  const handleArchive = useCallback(
    async ({ configId }: { configId: string }) => {
      const response = await deactivateCdqConfig({
        variables: {
          id: configId,
        },
      });

      const errors = response.data?.deactivateCdqConfig.errors;
      if (errors && errors.length) {
        toast({
          ...errorToastConfig,
          title: errors.join(', '),
        });
      } else {
        toast({
          ...successToastConfig,
          title: 'Successfully archived.',
        });
        refetchCdqConfigs();
      }
    },
    [deactivateCdqConfig, refetchCdqConfigs, toast],
  );

  // drawer state
  const drawer = useDisclosure();

  const activeCDQs: ConfiguredCdqGQLType[] =
    cdqConfigData?.getActiveCdqConfigs ?? [];

  if (isCdqConfigDataLoading || isDeactivateCdqConfigLoading) {
    return <Skeleton height="100%" width="100%" />;
  }

  const hasCDQs = activeCDQs.length > 0;

  return (
    <Box>
      <Flex alignItems="center">
        <Heading marginBottom="40px" display="inline-block">
          Collateral Data Quality
        </Heading>
        <Spacer />

        <CollateralDataQualityCheckAddMenu onClickAddCdqCheck={drawer.onOpen} />
      </Flex>
      {hasCDQs && (
        <CollateralDataQualityCheckMainContent
          activeCDQs={activeCDQs}
          handleArchive={handleArchive}
          receivablesRoutePrefix={receivablesRoutePrefix}
        />
      )}
      {!hasCDQs && <CollateralDataQualityCheckNoConfigsDisplay />}

      {/* Drawer for adding/editing CDQ */}
      {drawer.isOpen && (
        <CollateralDataQualityCheckAddEditCdqDrawerContainer
          creditFacilityId={undefined} // @dani update in the future to pass in cfid when we have cf-scoped cdq ui
          onClose={drawer.onClose}
          initialQuery={placeholderSQLString} // in add mode, pass in placeholder text. in edit mode, pass in existing when clicking an edit configured cdq (selectedCdqConfig)
          mode="add"
        />
      )}
    </Box>
  );
};
